// ── Stat Card ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, color = 'primary', sub }) {
  const colors = {
    primary: 'bg-[rgba(139,92,246,.12)] text-[#8B5CF6] ring-1 ring-[rgba(139,92,246,.25)]',
    green:   'bg-[rgba(52,211,153,.12)] text-[#34D399] ring-1 ring-[rgba(52,211,153,.25)]',
    amber:   'bg-[rgba(251,191,36,.12)] text-[#FBBF24] ring-1 ring-[rgba(251,191,36,.25)]',
    red:     'bg-[rgba(244,114,182,.12)] text-[#F472B6] ring-1 ring-[rgba(244,114,182,.25)]',
    blue:    'bg-[rgba(96,165,250,.12)] text-[#60A5FA] ring-1 ring-[rgba(96,165,250,.25)]',
  };
  return (
    <div className="card p-5 flex items-center gap-4 transition-colors hover:border-[rgba(139,92,246,.35)]">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${colors[color]}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-display font-bold tracking-tight text-ink-text">{value ?? '—'}</p>
        <p className="text-sm text-gray-500">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ label, color = 'gray' }) {
  const colors = {
    gray:   'bg-white/[.06] text-ink-muted ring-1 ring-white/10',
    green:  'bg-[rgba(52,211,153,.12)] text-[#34D399] ring-1 ring-[rgba(52,211,153,.3)]',
    amber:  'bg-[rgba(251,191,36,.12)] text-[#FBBF24] ring-1 ring-[rgba(251,191,36,.3)]',
    red:    'bg-[rgba(244,114,182,.12)] text-[#F472B6] ring-1 ring-[rgba(244,114,182,.3)]',
    blue:   'bg-[rgba(96,165,250,.12)] text-[#60A5FA] ring-1 ring-[rgba(96,165,250,.3)]',
    purple: 'bg-[rgba(167,139,250,.12)] text-[#A78BFA] ring-1 ring-[rgba(167,139,250,.3)]',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>
      {label}
    </span>
  );
}

// ── Status badge helper ───────────────────────────────────────────────────────
export function StatusBadge({ status }) {
  const map = {
    active:        { label: 'Active',        color: 'green'  },
    inactive:      { label: 'Inactive',      color: 'gray'   },
    assigned:      { label: 'Assigned',      color: 'blue'   },
    under_repair:  { label: 'Under Repair',  color: 'amber'  },
    pending:       { label: 'Pending',       color: 'amber'  },
    approved:      { label: 'Approved',      color: 'green'  },
    rejected:      { label: 'Rejected',      color: 'red'    },
    in_progress:   { label: 'In Progress',   color: 'blue'   },
    completed:     { label: 'Completed',     color: 'green'  },
    cancelled:     { label: 'Cancelled',     color: 'gray'   },
    valid:         { label: 'Valid',         color: 'green'  },
    expiring_soon: { label: 'Expiring Soon', color: 'amber'  },
    expired:       { label: 'Expired',       color: 'red'    },
    low:           { label: 'Low',           color: 'green'  },
    medium:        { label: 'Medium',        color: 'amber'  },
    high:          { label: 'High',          color: 'red'    },
    critical:      { label: 'Critical',      color: 'red'    },
    issued:        { label: 'Issued',        color: 'blue'   },
    returned:      { label: 'Returned',      color: 'green'  },
  };
  const s = map[status] || { label: status, color: 'gray' };
  return <Badge label={s.label} color={s.color} />;
}

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', onClick, type = 'button', disabled, className = '' }) {
  const variants = {
    primary: 'bg-[#6D3FE0] hover:bg-[#7C4DEE] text-white font-semibold shadow-[0_0_22px_-6px_rgba(139,92,246,.7)]',
    danger:  'bg-[rgba(244,114,182,.14)] hover:bg-[rgba(244,114,182,.24)] text-[#F472B6] ring-1 ring-[rgba(244,114,182,.35)]',
    ghost:   'bg-white/[.04] hover:bg-white/[.09] text-ink-text ring-1 ring-white/10',
    success: 'bg-[#0F8F63] hover:bg-[#0C7A54] text-white font-semibold',
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-2.5 text-base',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-2 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]} ${sizes[size]} ${className}`}>
      {children}
    </button>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────
export function Input({ label, error, ...props }) {
  return (
    <div>
      {label && <label className="block text-xs font-medium uppercase tracking-wide text-ink-muted mb-1.5">{label}</label>}
      <input
        {...props}
        className={`w-full px-3 py-2 text-sm rounded-lg outline-none transition-all bg-[#1B1640]
          text-ink-text ring-1 focus:ring-2 focus:ring-[#8B5CF6]
          ${error ? 'ring-[rgba(244,114,182,.5)]' : 'ring-[#3E3573] hover:ring-[#544A8C]'}`}
      />
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

// ── Select ────────────────────────────────────────────────────────────────────
export function Select({ label, error, children, ...props }) {
  return (
    <div>
      {label && <label className="block text-xs font-medium uppercase tracking-wide text-ink-muted mb-1.5">{label}</label>}
      <select
        {...props}
        className={`w-full px-3 py-2 text-sm rounded-lg outline-none transition-all bg-[#1B1640]
          text-ink-text ring-1 focus:ring-2 focus:ring-[#8B5CF6]
          ${error ? 'ring-[rgba(244,114,182,.5)]' : 'ring-[#3E3573] hover:ring-[#544A8C]'}`}>
        {children}
      </select>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, size = 'md' }) {
  if (!open) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className={`bg-ink-surface ring-1 ring-ink-line rounded-2xl shadow-lifted w-full ${sizes[size]} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line">
          <h3 className="text-base font-display font-semibold text-ink-text">{title}</h3>
          <button onClick={onClose} className="text-ink-faint hover:text-[#8B5CF6] text-xl leading-none transition-colors">×</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────
export function Table({ headers, children, empty = 'No records found' }) {
  return (
    <div className="overflow-x-auto rounded-xl ring-1 ring-ink-line bg-ink-surface">
      <table className="w-full text-sm">
        <thead className="bg-ink-raised text-ink-muted text-xs uppercase tracking-wider">
          <tr>{headers.map((h,i) => <th key={i} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-ink-line">
          {children || (
            <tr><td colSpan={headers.length} className="px-4 py-10 text-center text-ink-faint">{empty}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Page Header ───────────────────────────────────────────────────────────────
export function PageHeader({ title, sub, action }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h2 className="text-xl font-display font-bold tracking-tight text-ink-text">{title}</h2>
        {sub && <p className="text-sm text-gray-500 mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Loading ───────────────────────────────────────────────────────────────────
export function Loading() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 rounded-full animate-spin border-[3px] border-[#3E3573] border-t-[#8B5CF6] shadow-[0_0_18px_-4px_rgba(139,92,246,.7)]" />
    </div>
  );
}

// ── Alert ─────────────────────────────────────────────────────────────────────
export function Alert({ type = 'error', message }) {
  const styles = {
    error:   'bg-[rgba(244,114,182,.10)] border-[rgba(244,114,182,.3)] text-[#F472B6]',
    success: 'bg-[rgba(52,211,153,.10)] border-[rgba(52,211,153,.3)] text-[#34D399]',
    warning: 'bg-[rgba(251,191,36,.10)]  border-[rgba(251,191,36,.3)]  text-[#FBBF24]',
  };
  return (
    <div className={`border rounded-lg px-4 py-3 text-sm ${styles[type]}`}>
      {message}
    </div>
  );
}
